import React, { createContext, useContext, useState, ReactNode } from 'react';

// Snippet types
export interface SnippetDialogData {
  id?: string;
  name: string;
  code: string;
  language: string;
  category_id: string | null;
  tags: string[];
  attachments: string[];
}

// Tip types
export interface TipDialogData {
  id?: string;
  name: string;
  content: string;
  category_id: string | null;
  tags: string[];
  attachments: string[];
}

// Document types
export interface DocumentDialogData {
  id?: string;
  name: string;
  link: string | null;
  category_id: string | null;
  tags: string[];
  images: string[];
}

// Event types
export interface EventDialogData {
  id?: string;
  name: string;
  description: string;
  link: string;
}

type DialogType = 'snippet' | 'tip' | 'document' | 'event' | null;

interface DocHubDialogContextType {
  // Current dialog state
  dialogType: DialogType;
  isOpen: boolean;
  
  // Snippet dialog
  snippetData: SnippetDialogData | null;
  openSnippetDialog: (data?: Partial<SnippetDialogData>) => void;
  
  // Tip dialog
  tipData: TipDialogData | null;
  openTipDialog: (data?: Partial<TipDialogData>) => void;
  
  // Document dialog
  documentData: DocumentDialogData | null;
  openDocumentDialog: (data?: Partial<DocumentDialogData>) => void;
  
  // Event dialog
  eventData: EventDialogData | null;
  openEventDialog: (data?: Partial<EventDialogData>) => void;
  
  // Close dialog
  closeDialog: () => void;
  
  // Callbacks
  onSuccess?: () => void;
  setOnSuccess: (callback: () => void) => void;
}

const DocHubDialogContext = createContext<DocHubDialogContextType | undefined>(undefined);

const defaultSnippetData: SnippetDialogData = {
  name: '',
  code: '',
  language: '',
  category_id: null,
  tags: [],
  attachments: [],
};

const defaultTipData: TipDialogData = {
  name: '',
  content: '',
  category_id: null,
  tags: [],
  attachments: [],
};

const defaultDocumentData: DocumentDialogData = {
  name: '',
  link: null,
  category_id: null,
  tags: [],
  images: [],
};

const defaultEventData: EventDialogData = {
  name: '',
  description: '',
  link: '',
};

interface DocHubDialogProviderProps {
  children: ReactNode;
}

export const DocHubDialogProvider = ({ children }: DocHubDialogProviderProps) => {
  const [dialogType, setDialogType] = useState<DialogType>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [snippetData, setSnippetData] = useState<SnippetDialogData | null>(null);
  const [tipData, setTipData] = useState<TipDialogData | null>(null);
  const [documentData, setDocumentData] = useState<DocumentDialogData | null>(null);
  const [eventData, setEventData] = useState<EventDialogData | null>(null);
  const [onSuccess, setOnSuccessCallback] = useState<(() => void) | undefined>();

  const openSnippetDialog = (data?: Partial<SnippetDialogData>) => {
    setSnippetData({ ...defaultSnippetData, ...data });
    setDialogType('snippet');
    setIsOpen(true);
  };

  const openTipDialog = (data?: Partial<TipDialogData>) => {
    setTipData({ ...defaultTipData, ...data });
    setDialogType('tip');
    setIsOpen(true);
  };

  const openDocumentDialog = (data?: Partial<DocumentDialogData>) => {
    setDocumentData({ ...defaultDocumentData, ...data });
    setDialogType('document');
    setIsOpen(true);
  };

  const openEventDialog = (data?: Partial<EventDialogData>) => {
    setEventData({ ...defaultEventData, ...data });
    setDialogType('event');
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setDialogType(null);
  };

  const setOnSuccess = (callback: () => void) => {
    setOnSuccessCallback(() => callback);
  };

  const value: DocHubDialogContextType = {
    dialogType,
    isOpen,
    snippetData,
    openSnippetDialog,
    tipData,
    openTipDialog,
    documentData,
    openDocumentDialog,
    eventData,
    openEventDialog,
    closeDialog,
    onSuccess,
    setOnSuccess,
  };

  return (
    <DocHubDialogContext.Provider value={value}>
      {children}
    </DocHubDialogContext.Provider>
  );
};

export const useDocHubDialog = () => {
  const context = useContext(DocHubDialogContext);
  if (context === undefined) {
    throw new Error('useDocHubDialog must be used within a DocHubDialogProvider');
  }
  return context;
};
